import { useMemo, useState } from "react";
import { Loader2, Megaphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MarkdownReact } from "@/components/MarkdownReact";
import { useUserDisplayNames } from "@/store/teamsStore";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import { AnnouncementEditorRow } from "./AnnouncementEditorRow";

/**
 * Announcements composer and list: markdown body with live preview, pin
 * option, and per-post edit/pin/delete.
 */
export function AnnouncementsEditorSection() {
  const announcementMap = useOnboardingStore((state) => state.announcementMap);
  const announcementCreateState = useOnboardingStore(
    (state) => state.announcementCreateState,
  );
  const createAnnouncement = useOnboardingStore(
    (state) => state.createAnnouncement,
  );
  const userNames = useUserDisplayNames();

  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(false);

  const announcements = useMemo(
    () =>
      [...announcementMap.values()].sort(
        (a, b) =>
          Number(b.pinned) - Number(a.pinned) ||
          b.createdAt.getTime() - a.createdAt.getTime(),
      ),
    [announcementMap],
  );

  const handlePost = async () => {
    const trimmed = body.trim();
    if (!trimmed) {
      return;
    }
    const created = await createAnnouncement({ body: trimmed, pinned });
    if (created) {
      setBody("");
      setPinned(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Megaphone className="h-4 w-4" />
          Announcements
        </CardTitle>
        <CardDescription>
          Messages shown at the top of the onboarding tab. Markdown supported.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Textarea
            placeholder="Write an announcement…"
            value={body}
            onChange={(event) => setBody(event.target.value)}
            rows={3}
          />
          {body.trim() && (
            <div className="rounded-md border bg-muted/30 p-3">
              <p className="pb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                Preview
              </p>
              <div className="prose prose-sm max-w-none dark:prose-invert">
                <MarkdownReact>{body}</MarkdownReact>
              </div>
            </div>
          )}
          <div className="flex items-center gap-4">
            <Button
              onClick={handlePost}
              disabled={announcementCreateState.isSaving || !body.trim()}
            >
              Post
              {announcementCreateState.isSaving && (
                <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              )}
            </Button>
            <div className="flex items-center gap-2">
              <Checkbox
                id="announcement-pinned"
                checked={pinned}
                onCheckedChange={(checked) => setPinned(checked === true)}
              />
              <Label htmlFor="announcement-pinned" className="text-sm">
                Pin to top
              </Label>
            </div>
            {announcementCreateState.error && (
              <span className="text-sm text-destructive">
                {announcementCreateState.error}
              </span>
            )}
          </div>
        </div>

        <div className="space-y-2">
          {announcements.map((announcement) => (
            <AnnouncementEditorRow
              key={announcement.id}
              announcement={announcement}
              authorName={userNames.get(announcement.authorUserId)}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
